import Head from 'next/head';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { Dispatch, memo, useEffect, useRef, useState } from 'react';
import Hotkeys from 'react-hot-keys';
import InfiniteViewer from 'react-infinite-viewer';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import GameAPI from '../../frontendServices/gameAPI';
import useAccessBadge from '../../hooks/useAccessBadge';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import useDesignerLogic, {
  DesignerDataStateProvider,
  isOngoingGameModalVisibleSelector,
  useDesignerDataContext,
} from '../../hooks/useDesignerLogic';
import {
  DesignerUIStateAction,
  DesignerUIStateActions,
  DesignerUIStateProvider,
  useDesignerUIContext,
  useDesignerUIState,
} from '../../hooks/useDesignerUIState';
import useFactCheckPopup from '../../hooks/useFactcheckPopup';
import useRoutePrefetch from '../../hooks/useRoutePrefetch';
import useRouterMatch from '../../hooks/useRouterMatch';
import DesginerMultiTabHandlerProvider from '../../providers/DesginerMultiTabHandlerProvider';
import SocketProvider from '../../providers/SocketProvider';
import TimesyncProvider from '../../providers/TimesyncProvider';
import { MOBILE_TO_DESKTOP_BREAKPOINT_PX } from '../../utils/creator';
import { zIndexes } from '../../utils/zIndexes';
import DesignerSkeletonLoader from '../_molecules/DesignerSkeletonLoader';
import DesignerSlideEditor from '../_organisms/DesignerSlideEditor';
import DesignerTabPane from '../_organisms/DesignerTabPane';
import DesignerTabs from '../_organisms/DesignerTabs';
import DesignerMobileTabs from '../_organisms/DesignerTabs/DesignerMobileTabs';
import DesignerTopBar from '../_organisms/DesignerTopBar/DesignerTopBar';
import FloatingSlideOverviewXY from '../_organisms/FloatingSlideOverviewXY/FloatingSlideOverviewXY';
import { createTextElement, SLIDE_ASPECT } from '../SlideComponents/slideUtil';
import { DesignerTopBarContainer } from '../styled/CreatorTopBar';

const DesignerKeyboardShortcuts = () => {
  const isVerticalNavigation = useBreakPoint(900);

  const showSettingsModal = useDesignerUIContext(c => c.state.showSettingsModal);
  const arrowKeyIsDisabled = useDesignerUIContext(
    c => c.state.isArrowKeyNavigationDisabled,
  );

  const dispatch = useDesignerUIContext(context => context.dispatch);

  const isOnGoingGameModalVisible = useDesignerDataContext(
    isOngoingGameModalVisibleSelector,
  );

  const handleLeftArrowKey = useDesignerDataContext(
    context => context.handleLeftArrowKey,
  );
  const handleRightArrowKey = useDesignerDataContext(
    context => context.handleRightArrowKey,
  );
  const handleUpArrowKey = useDesignerDataContext(context => context.handleUpArrowKey);
  const handleDownArrowKey = useDesignerDataContext(
    context => context.handleDownArrowKey,
  );
  const editingTextElementId = useDesignerUIContext(
    context => context.state.textElementIdBeingEdited,
  );
  const handleUndo = useDesignerDataContext(context => context.handleUndo);
  const handleRedo = useDesignerDataContext(context => context.handleRedo);
  const handleKeyBoardCopy = useDesignerDataContext(
    context => context.handleKeyBoardCopy,
  );
  const handleKeyBoardCut = useDesignerDataContext(context => context.handleKeyBoardCut);
  const handleKeyBoardPaste = useDesignerDataContext(
    context => context.handleKeyBoardPaste,
  );
  const handleKeyBoardDelete = useDesignerDataContext(
    context => context.handleKeyBoardDelete,
  );

  const handleKeyBoardDuplicate = useDesignerDataContext(
    context => context.handleKeyBoardDuplicate,
  );
  const selectedElements = useDesignerUIContext(
    context => context.state.selectedElements,
  );
  const onElementsChange = useDesignerDataContext(
    context => context.handleElementsChange,
  );
  const handleKeyboardCreateNewSlide = useDesignerDataContext(
    context => context.handleCreateNewSlide,
  );
  const onAddElements = useDesignerDataContext(context => context.handleElementsAdd);
  const localTemplate = useDesignerDataContext(context => context.state.localTemplate);
  const handleAddTextElement = () => {
    const newElement = createTextElement({
      fontSize: 80,
      width: SLIDE_ASPECT.width * 0.5,
      textValue: '',
      textColor: theme.colors.black,
    });
    onAddElements([newElement]);
  };

  useEffect(() => {
    if (showSettingsModal || isOnGoingGameModalVisible) {
      dispatch({ type: DesignerUIStateActions.disableArrowKeyNavigation });
    }
    return () => {
      dispatch({ type: DesignerUIStateActions.enableArrowKeyNavigation });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerticalNavigation, showSettingsModal, isOnGoingGameModalVisible]);

  return (
    <>
      <Hotkeys
        keyName="left"
        allowRepeat={true}
        onKeyDown={(_, evnt) => {
          handleLeftArrowKey(evnt);
        }}
        disabled={arrowKeyIsDisabled}
      />
      <Hotkeys
        keyName="right"
        allowRepeat={true}
        onKeyDown={(_, evnt) => {
          handleRightArrowKey(evnt);
        }}
        disabled={arrowKeyIsDisabled}
      />
      <Hotkeys
        keyName="up"
        allowRepeat={true}
        onKeyDown={(_, evnt) => {
          // if you have selected elements, don't navigate as we want to move the elements. See ProxySlide for more element movement
          handleUpArrowKey(evnt);
        }}
        disabled={arrowKeyIsDisabled}
      />
      <Hotkeys
        keyName="down"
        allowRepeat={true}
        onKeyDown={(_, evnt) => {
          // if you have selected elements, don't navigate as we want to move the elements. See ProxySlide for more element movement
          handleDownArrowKey(evnt);
        }}
        disabled={arrowKeyIsDisabled}
      />
      <Hotkeys
        keyName="ctrl+z,command+z"
        onKeyDown={(_, evnt) => {
          evnt.preventDefault();
          handleUndo();
        }}
        disabled={arrowKeyIsDisabled}
        allowRepeat={true}
      />
      <Hotkeys
        keyName="ctrl+shift+z,command+shift+z"
        onKeyDown={(_, evnt) => {
          evnt.preventDefault();
          handleRedo();
        }}
        disabled={arrowKeyIsDisabled}
        allowRepeat={true}
      />
      <Hotkeys
        keyName="ctrl+c,command+c"
        onKeyDown={(_, evnt) => {
          evnt.preventDefault();
          handleKeyBoardCopy();
        }}
        disabled={arrowKeyIsDisabled}
      />
      <Hotkeys
        keyName="ctrl+x,command+x"
        onKeyDown={(_, evnt) => {
          evnt.preventDefault();
          handleKeyBoardCut();
        }}
        disabled={arrowKeyIsDisabled}
      />
      <Hotkeys
        keyName="ctrl+v,command+v"
        onKeyDown={(_, evnt) => {
          evnt.preventDefault();
          handleKeyBoardPaste();
        }}
        disabled={arrowKeyIsDisabled}
      />

      <Hotkeys
        keyName="t"
        onKeyDown={(_, evnt) => {
          evnt.preventDefault();
          handleAddTextElement();
        }}
        disabled={!!editingTextElementId}
      />
      {!!localTemplate?.aiContent?.prompts?.length && !!!selectedElements && (
        <>
          <Hotkeys
            keyName="ctrl+Backspace,command+Backspace"
            onKeyDown={(_, evnt) => {
              evnt.preventDefault();
              handleKeyBoardDelete();
            }}
          />
          <Hotkeys
            keyName="ctrl+Delete,command+Delete"
            onKeyDown={(_, evnt) => {
              evnt.preventDefault();
              handleKeyBoardDelete();
            }}
          />
        </>
      )}
      {(!!!localTemplate?.aiContent?.prompts?.length || !!selectedElements) && (
        <>
          <Hotkeys
            keyName="Backspace,ctrl+Backspace,command+Backspace"
            onKeyDown={(_, evnt) => {
              evnt.preventDefault();
              handleKeyBoardDelete();
            }}
          />
          <Hotkeys
            keyName="Delete,ctrl+Delete,command+Delete"
            onKeyDown={(_, evnt) => {
              evnt.preventDefault();
              handleKeyBoardDelete();
            }}
          />
        </>
      )}
      <Hotkeys
        keyName="ctrl+d,command+d"
        onKeyDown={(_, e) => {
          e.preventDefault();
          e.stopPropagation();
          handleKeyBoardDuplicate();
        }}
      />
      <Hotkeys
        keyName="ctrl+Enter,command+Enter"
        onKeyDown={(_, evnt) => {
          evnt.preventDefault();
          handleKeyboardCreateNewSlide('blank');
        }}
      />
      <Hotkeys
        keyName="ctrl+down,command+down"
        allowRepeat={true}
        onKeyDown={(_, evnt) => {
          if (selectedElements) {
            evnt.preventDefault();
            onElementsChange({ zChange: 'down', updatedElements: selectedElements });
          }
        }}
      />
      <Hotkeys
        keyName="ctrl+up,command+up"
        allowRepeat={true}
        onKeyDown={(_, evnt) => {
          if (selectedElements) {
            evnt.preventDefault();
            onElementsChange({ zChange: 'up', updatedElements: selectedElements });
          }
        }}
      />
    </>
  );
};

const DesignerLoader = () => {
  const localTemplate = useDesignerDataContext(context => context.state.localTemplate);
  const isFetchingTemplate = useDesignerDataContext(
    context => context.state.isFetchingTemplate,
  );

  return <>{isFetchingTemplate && !localTemplate && <DesignerSkeletonLoader />}</>;
};

const FactCheckNotifier = () => {
  const { accessBadge: userAccessBadge } = useAccessBadge();

  const localTemplate = useDesignerDataContext(context => context.state.localTemplate);

  useFactCheckPopup({
    templateId: localTemplate?.id,
    isAiGenerated: !!localTemplate?.metadata?.['aiTopic'],
    isOwner: userAccessBadge?.user.id === localTemplate?.creator.uid,
  });

  return null;
};

const DesignerDocumentHead = () => {
  const localTemplate = useDesignerDataContext(context => context.state.localTemplate);

  return (
    <Head>
      {localTemplate && (
        <title>
          Designer
          {localTemplate.title ? ` | ${localTemplate.title}` : ''}
        </title>
      )}
    </Head>
  );
};

const PreventPullToRefresh = () => {
  return (
    <style jsx global>{`
      /* prevent pull-to-refresh for Safari 16+ */
      @media screen and (pointer: coarse) {
        @supports (-webkit-backdrop-filter: blur(1px)) and (overscroll-behavior-y: none) {
          html {
            min-height: 100.3%;
            overscroll-behavior-y: none;
          }
        }
      }
      /* prevent pull-to-refresh for Safari 9~15 */
      @media screen and (pointer: coarse) {
        @supports (-webkit-backdrop-filter: blur(1px)) and
          (not (overscroll-behavior-y: none)) {
          html {
            height: 100%;
            overflow: hidden;
          }
          body {
            margin: 0px;
            max-height: 100%; /* or if body has default margin */
            overflow: auto;
            -webkit-overflow-scrolling: touch;
          }
          /* in this case to disable pinch-zoom, set  */
        }
      }

      /* prevent pull-to-refresh for Chrome 63+ */
      body {
        overscroll-behavior-y: none;
      }
    `}</style>
  );
};

const Designer = () => {
  const isDesktop = useBreakPoint(MOBILE_TO_DESKTOP_BREAKPOINT_PX);
  useRoutePrefetch(`/[tenantId]/games/[gameId]/live`, undefined, {
    priority: true,
  });
  const { tenantId, templateId } = useRouterMatch();
  const designerSlideEditorParentRef = useRef<HTMLDivElement>(null);
  const editingAreaRef = useRef<HTMLDivElement>(null);
  const infiniteViewerRef = useRef<InfiniteViewer>(null);
  const showTabPaneFullWidth = useDesignerUIContext(c => c.state.showTabPaneFullWidth);
  const isLeftTabCollapsed = useDesignerUIContext(c => c.state.isLeftTabCollapsed);
  const isSlideListCollapsed = useDesignerUIContext(c => c.state.isSlideListCollapsed);
  const setUploadedSlidesCache = useDesignerUIContext(c => c.setUploadedSlidesCache);

  const zoom = useDesignerUIContext(context => context.state.zoom);

  const setGeneratorTabState = useDesignerUIContext(c => c.setGeneratorTabState);
  const session = useSession();
  const [gameAPI, setGameAPI] = useState<GameAPI | undefined | null>(undefined);

  useEffect(() => {
    return () => {
      if (gameAPI) {
        gameAPI.disconnect();
      }
    };
  }, [gameAPI]);

  useEffect(() => {
    if (session.data?.idToken && !gameAPI) {
      const gameAPI = new GameAPI(session.data?.idToken as string);
      setGameAPI(gameAPI);
    }
  }, [session, gameAPI]);

  if (!gameAPI) {
    return (
      <PageContainer>
        <DesignerTopBarContainer />
        <DesignerSkeletonLoader />
      </PageContainer>
    );
  }

  return (
    <DesginerMultiTabHandlerProvider>
      <SocketProvider gameAPI={gameAPI}>
        <TimesyncProvider gameAPI={gameAPI}>
          <PageContainer>
            <PreventPullToRefresh />
            <DesignerKeyboardShortcuts />
            <DesignerDocumentHead />
            <FactCheckNotifier />
            <DesignerTopBar tenantId={tenantId} templateId={templateId} />
            <DesignerLoader />
            <MainContainer
              $isTwoColumns={showTabPaneFullWidth}
              $isLeftTabCollapsed={isLeftTabCollapsed}
            >
              {isDesktop && (
                <>
                  <DesignerTabs />
                  <DesignerTabPane
                    templateId={templateId}
                    setGeneratorTabState={setGeneratorTabState}
                    setUploadedSlidesCache={setUploadedSlidesCache}
                  />
                  {!showTabPaneFullWidth && (
                    <SlideAndSlideListContainer
                      ref={designerSlideEditorParentRef}
                      $isSlideListCollapsed={isSlideListCollapsed}
                    >
                      <DesignerSlideEditor
                        infiniteViewerRef={infiniteViewerRef}
                        editingAreaRef={editingAreaRef}
                        zoom={zoom}
                      />
                      <FloatingSlideOverviewXY
                        designerSlideEditorParentRef={designerSlideEditorParentRef}
                        infiniteViewerRef={infiniteViewerRef}
                        editingAreaRef={editingAreaRef}
                        zoom={zoom}
                      />
                    </SlideAndSlideListContainer>
                  )}
                </>
              )}

              {!isDesktop && (
                <>
                  {!showTabPaneFullWidth && (
                    <SlideAndSlideListContainer
                      ref={designerSlideEditorParentRef}
                      $isSlideListCollapsed={isSlideListCollapsed}
                    >
                      <DesignerSlideEditor
                        infiniteViewerRef={infiniteViewerRef}
                        editingAreaRef={editingAreaRef}
                        zoom={zoom}
                      />
                      <FloatingSlideOverviewXY
                        designerSlideEditorParentRef={designerSlideEditorParentRef}
                        infiniteViewerRef={infiniteViewerRef}
                        editingAreaRef={editingAreaRef}
                        zoom={zoom}
                      />
                    </SlideAndSlideListContainer>
                  )}
                  <DesignerMobileTabs
                    templateId={templateId}
                    setGeneratorTabState={setGeneratorTabState}
                    setUploadedSlidesCache={setUploadedSlidesCache}
                  />
                </>
              )}
            </MainContainer>
          </PageContainer>
        </TimesyncProvider>
      </SocketProvider>
    </DesginerMultiTabHandlerProvider>
  );
};

const SlideAndSlideListContainer = styled.div<{ $isSlideListCollapsed: boolean }>`
  width: 100%;
  height: 100%;
  max-height: calc(100vh - 65px);
  display: grid;
  grid-template-rows: auto ${({ $isSlideListCollapsed }) =>
      $isSlideListCollapsed ? '30px' : '140px'};
  background: ${theme.colors.creatorBackgroundGrey};

  @media (max-width: ${MOBILE_TO_DESKTOP_BREAKPOINT_PX}px) {
    padding: 6px;
    height: calc(100% - 76px);
  }
`;

const PageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${theme.colors.creatorBackgroundGrey};
`;

export const ActionBarContainer = styled.div`
  width: 100%;
  display: flex;
  position: relative;
  z-index: ${zIndexes('TOPBAR')};
  justify-content: space-between;
  height: 60px;
  padding: 5px 15px 5px 15px;
  background: ${theme.colors.creatorTopbarGradient};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
`;

const MainContainer = styled.div<{
  $isTwoColumns?: boolean;
  $isLeftTabCollapsed: boolean;
}>`
  width: 100%;
  display: grid;
  grid-template-columns: 90px 350px 1fr;
  height: calc(100% - 60px);
  position: relative;
  overflow: hidden;
  transition: unset;
  
  ${({ $isLeftTabCollapsed }) =>
    $isLeftTabCollapsed &&
    css`
      grid-template-columns: 90px 0 1fr;
      gap: 5px;
      transition: grid-template-columns 0.2s ease-in;
    `}
    
  
  ${({ $isTwoColumns }) =>
    $isTwoColumns &&
    css`
      grid-template-columns: 90px auto;
    `}



  @media (max-width: ${MOBILE_TO_DESKTOP_BREAKPOINT_PX}px) {
    display: flex;
    flex-direction: column;
    padding: 0px 0px 0px 0px;
    gap: 0px;
  }
`;

const MemoisedDesigner = memo(Designer);

const DesignerDataStateWrapper = ({
  dispatch,
}: {
  dispatch: Dispatch<DesignerUIStateAction>;
}) => {
  const {
    localTemplateDispatch,
    localTemplate,
    handleKeyBoardCopy,
    handleElementsDelete,
    handleElementsChange,
    handleCreateNewSlide,
    handleSlideTypeChanged,
    handleKeyBoardCut,
    handleKeyBoardPaste,
    handleKeyBoardDelete,
    handleKeyBoardDuplicate,
    handleSlideDelete,
    handleSlideCopy,
    handleExit,
    handleSlideChange,
    handleAllSlideChange,
    addSlide,
    addTranslatedSlides,
    handleElementsAdd,
    handleRedo,
    handleUndo,
    canUndo,
    canRedo,
    handleLeftArrowKey,
    handleRightArrowKey,
    handleDownArrowKey,
    handleUpArrowKey,
    isFetchingTemplate,
    onSlideSelected,
    handleSelectedTabChange,
    handleExportAsPdf,
  } = useDesignerLogic(dispatch);

  return (
    <DesignerDataStateProvider
      value={{
        dispatch: localTemplateDispatch,
        state: {
          localTemplate,
          isFetchingTemplate,
          canUndo,
          canRedo,
        },
        handleUndo,
        handleRedo,
        handleLeftArrowKey,
        handleRightArrowKey,
        handleDownArrowKey,
        handleUpArrowKey,
        handleKeyBoardCopy,
        handleKeyBoardCut,
        handleKeyBoardPaste,
        handleKeyBoardDelete,
        handleKeyBoardDuplicate,
        handleExit,
        handleSlideChange,
        handleElementsChange,
        addSlide,
        addTranslatedSlides,
        handleAllSlideChange,
        handleElementsAdd,
        handleSlideTypeChanged,
        handleCreateNewSlide,
        handleElementsDelete,
        onSlideSelected,
        handleSlideDelete,
        handleSlideCopy,
        handleSelectedTabChange,
        handleExportAsPdf,
      }}
    >
      <MemoisedDesigner />
    </DesignerDataStateProvider>
  );
};

const MemoisedDesignerDataStateWrapper = memo(DesignerDataStateWrapper);

const DesignerStateWrapper = () => {
  const router = useRouter();
  const templateId = router.query.templateId as string;
  const {
    dispatch,
    state,
    handleShowSettingsModal,
    setGeneratorTabState,
    handleShowSettingsModalCancel,
    setUploadedSlidesCache,
    handleMediaSelected,
    handleCloseDrawer,
    setSelectedElements,
    setSelectedPromptId,
    deselectElements,
    handleSetForceLoadingState,
    setIsUserIdle,
  } = useDesignerUIState(templateId);

  return (
    <DesignerUIStateProvider
      value={{
        dispatch,
        state,
        handleShowSettingsModal,
        setGeneratorTabState,
        handleShowSettingsModalCancel,
        setUploadedSlidesCache,
        handleMediaSelected,
        handleCloseDrawer,
        setSelectedElements,
        setSelectedPromptId,
        deselectElements,
        handleSetForceLoadingState,
        setIsUserIdle,
      }}
    >
      <MemoisedDesignerDataStateWrapper dispatch={dispatch} />
    </DesignerUIStateProvider>
  );
};

const DesignerPageWrapper = () => {
  const session = useSession();

  if (session.status === 'authenticated') {
    return <DesignerStateWrapper />;
  }
  if (session.status === 'unauthenticated') {
    return <PublicDesignerPage />;
  }

  return null;
};

export default DesignerPageWrapper;

const PublicDesignerPage = () => {
  // Prevent rendering the whole creator page if the user is not authenticated
  const router = useRouter();
  const urlTenantId = router.query.tenantId;
  const templateId = router.query.templateId as string;

  useEffect(() => {
    router.replace(`/${urlTenantId}/lessons/${templateId}`);
  }, [router, templateId, urlTenantId]);

  return null;
};
